// Extracts styleframe candidates from a video file, entirely in the browser:
// the video is sampled at regular intervals, a small grayscale fingerprint of
// each sample is compared with the previous one, and a frame is captured at
// every detected cut. Nothing is sent anywhere until the admin keeps frames.

export interface FrameCandidate {
  id: string;
  time: number;
  blob: Blob;
  previewURL: string;
  width: number;
  height: number;
}

export interface SceneDetectOptions {
  /** Maximum number of frames returned. */
  maxFrames?: number;
  /** 0–255 mean pixel difference that counts as a cut. */
  threshold?: number;
  /** Seconds skipped after a cut before capturing (lets transitions settle). */
  settle?: number;
  /** Longest side of captured frames. */
  maxDimension?: number;
  onProgress?: (ratio: number, found: number) => void;
  signal?: AbortSignal;
}

const FINGERPRINT_W = 64;
const FINGERPRINT_H = 36;

function seek(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const onSeeked = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error("Lecture de la vidéo impossible à cet instant."));
    };
    const cleanup = () => {
      video.removeEventListener("seeked", onSeeked);
      video.removeEventListener("error", onError);
    };
    video.addEventListener("seeked", onSeeked);
    video.addEventListener("error", onError);
    video.currentTime = Math.min(time, Math.max(0, video.duration - 0.05));
  });
}

function loadVideo(src: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";
    video.crossOrigin = "anonymous";
    video.onloadeddata = () => resolve(video);
    video.onerror = () =>
      reject(new Error("Format vidéo non lisible par le navigateur (essayez un MP4 H.264)."));
    video.src = src;
  });
}

function fingerprint(ctx: CanvasRenderingContext2D, video: HTMLVideoElement): Uint8ClampedArray {
  ctx.drawImage(video, 0, 0, FINGERPRINT_W, FINGERPRINT_H);
  const { data } = ctx.getImageData(0, 0, FINGERPRINT_W, FINGERPRINT_H);
  const gray = new Uint8ClampedArray(FINGERPRINT_W * FINGERPRINT_H);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    gray[p] = (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
  }
  return gray;
}

function difference(a: Uint8ClampedArray, b: Uint8ClampedArray): number {
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += Math.abs(a[i] - b[i]);
  return sum / a.length;
}

function isMostlyBlank(gray: Uint8ClampedArray): boolean {
  let min = 255;
  let max = 0;
  for (let i = 0; i < gray.length; i++) {
    if (gray[i] < min) min = gray[i];
    if (gray[i] > max) max = gray[i];
  }
  return max - min < 12;
}

async function capture(video: HTMLVideoElement, maxDimension: number): Promise<{ blob: Blob; width: number; height: number }> {
  const scale = Math.min(1, maxDimension / Math.max(video.videoWidth, video.videoHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  canvas.getContext("2d")!.drawImage(video, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.9));
  if (!blob) throw new Error("Capture d'image impossible.");
  return { blob, width: canvas.width, height: canvas.height };
}

export async function detectStyleframes(
  file: File | string,
  {
    maxFrames = 24,
    threshold = 28,
    settle = 0.4,
    maxDimension = 1920,
    onProgress,
    signal,
  }: SceneDetectOptions = {},
): Promise<FrameCandidate[]> {
  const src = typeof file === "string" ? file : URL.createObjectURL(file);
  const video = await loadVideo(src);
  const duration = video.duration;
  if (!isFinite(duration) || duration <= 0) throw new Error("Durée de la vidéo inconnue.");

  const step = Math.max(0.2, Math.min(1, duration / 400));
  const canvas = document.createElement("canvas");
  canvas.width = FINGERPRINT_W;
  canvas.height = FINGERPRINT_H;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;

  const cuts: number[] = [];
  let previous: Uint8ClampedArray | null = null;
  let lastCut = -Infinity;

  try {
    for (let t = 0; t < duration; t += step) {
      if (signal?.aborted) throw new DOMException("Analyse annulée", "AbortError");
      await seek(video, t);
      const current = fingerprint(ctx, video);
      const isCut = !previous || difference(previous, current) > threshold;
      if (isCut && t - lastCut > 1 && !isMostlyBlank(current)) {
        cuts.push(t);
        lastCut = t;
      }
      previous = current;
      onProgress?.(Math.min(0.8, (t / duration) * 0.8), cuts.length);
    }

    let times = cuts.map((c) => Math.min(duration - 0.1, c + settle));
    if (times.length < 6) {
      // Few cuts (long shots): keep them and fill with evenly spaced frames.
      const count = Math.min(maxFrames, 12);
      const spaced = Array.from({ length: count }, (_, i) => ((i + 0.5) * duration) / count);
      spaced.forEach((t) => {
        if (times.length < count && times.every((existing) => Math.abs(existing - t) > 1)) times.push(t);
      });
      times.sort((a, b) => a - b);
    } else if (times.length > maxFrames) {
      const stride = times.length / maxFrames;
      times = Array.from({ length: maxFrames }, (_, i) => times[Math.floor(i * stride)]);
    }

    const frames: FrameCandidate[] = [];
    for (let i = 0; i < times.length; i++) {
      if (signal?.aborted) throw new DOMException("Analyse annulée", "AbortError");
      await seek(video, times[i]);
      const { blob, width, height } = await capture(video, maxDimension);
      frames.push({
        id: `frame-${Math.round(times[i] * 1000)}`,
        time: times[i],
        blob,
        previewURL: URL.createObjectURL(blob),
        width,
        height,
      });
      onProgress?.(0.8 + ((i + 1) / times.length) * 0.2, frames.length);
    }
    return frames;
  } finally {
    video.removeAttribute("src");
    video.load();
    if (typeof file !== "string") URL.revokeObjectURL(src);
  }
}

export function formatTimecode(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/** Reads a local video's duration without uploading it. */
export function readVideoDuration(file: File): Promise<number | undefined> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => {
      resolve(isFinite(video.duration) ? Math.round(video.duration) : undefined);
      URL.revokeObjectURL(url);
    };
    video.onerror = () => {
      resolve(undefined);
      URL.revokeObjectURL(url);
    };
    video.src = url;
  });
}
